/**
 * <UpcomingAssessmentsSection> — the "Contrôles à venir" block of the parent home (template, L0.1).
 *
 * Mirrors the legacy assessments block in `ParentSubscribeScreen`: a title (no header button), the
 * list of `<SchoolAssessmentAccordion>`s, then the "Ajouter un contrôle" button rendered BELOW the
 * list (legacy places it after the cards, `outlined` when there are items and `contained` when the
 * list is empty). Thin domain wrapper over the `<List>` molecule (the add button is its footer).
 */
import React from "react"

import { LabeledIconButton } from "../../atoms/Buttons/LabeledIconButton"
import { List } from "../../molecules/List"
import { SchoolAssessmentAccordion } from "../../organisms/SchoolAssessmentAccordion"
import { type UISchoolAssessment } from "../../../ports/primary/ui/types"
import { Stack } from "../../atoms/containers/Stack"

export type UpcomingAssessmentsSectionProps = {
    assessments: UISchoolAssessment[]
    onAddAssessment?: () => void
    onEditAssessment?: (id: UISchoolAssessment["id"]) => void
    onDeleteAssessment?: (id: UISchoolAssessment["id"]) => void
    onAssessmentDetails?: (id: UISchoolAssessment["id"]) => void
}

export function UpcomingAssessmentsSection(props: UpcomingAssessmentsSectionProps) {
    const { assessments, onAddAssessment, onEditAssessment, onDeleteAssessment, onAssessmentDetails } = props
    const hasItems = assessments.length > 0

    const footer = (
        <Stack gap="2" align="center">
            <LabeledIconButton
                label="Ajouter un contrôle"
                iconName="plus"
                variant={hasItems ? "outline" : "solid"}
                onPress={onAddAssessment}
                fitContent={false}
            />
        </Stack>
    )

    return (
        <List title="Contrôles à venir" emptyMessage="Aucun contrôle à venir" footer={footer}>
            {assessments.map((assessment) => (
                <SchoolAssessmentAccordion
                    key={assessment.id}
                    assessment={assessment}
                    onEdit={() => onEditAssessment?.(assessment.id)}
                    onDelete={() => onDeleteAssessment?.(assessment.id)}
                    onDetails={() => onAssessmentDetails?.(assessment.id)}
                />
            ))}
        </List>
    )
}
